import React from 'react';
import Menu from '@mui/material/Menu';
import { Box, Button, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import ImageButton from '../ImageButton/ImageButton';

const StyledLogOutButton = styled(Button)(() => ({
  borderRadius: '10px',
  maxWidth: '150px',
  border: '2px solid #FF9A9A ',
  background: 'transparent',

  '& span': {
    background: 'linear-gradient(138deg, #6599A5 24.11%, #3B667E 83.2%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    fontFamily: 'Satoshi',
    fontSize: '17px',
    fontWeight: 700,
    lineHeight: '22px',
  },
  '&:hover': {
    border: '2px solid linear-gradient(175.46deg, #FF9A9A -9.81%, #FF5858 96.32%)',
  },
}));

export interface ILoggedUser {
  surname?: string;
  firstName: string;
  lastName: string;
  profilePic: string;
  email: string;
}

export enum UserType {
  CLIENT = 'client',
  COACH = 'coach',
  ADMIN = 'admin',
}

interface IUserProps {
  user: ILoggedUser;
  type: UserType;
  logoutHandler: () => void;
  isBreadcrumb?: boolean;
}

const User = ({ user, type, logoutHandler, isBreadcrumb }: IUserProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { t } = useTranslation();

  if (!user) {
    return null;
  }

  const getProfilePicUrl = () => {
    if (!user?.profilePic) return '';
    if (user.profilePic?.startsWith('https://')) return user.profilePic;
    return new URL(user.profilePic, process.env.REACT_APP_MEDIA_FILES_BASE_URL).href;
  };

  const renderAvatar = () => {
    if (user.profilePic) {
      return (
        <img
          src={getProfilePicUrl()}
          alt="user_img"
          style={{
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            objectFit: 'cover',
          }}
        />
      );
    }
    const initials = `${user.firstName ? user.firstName[0].toUpperCase() : ''}${
      user.lastName ? user.lastName[0].toUpperCase() : ''
    }`;
    return (
      <Box
        sx={{
          borderRadius: '50%',
          backgroundColor: 'secondary.main',
          color: 'white',
          width: '28px',
          height: '28px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {initials}
      </Box>
    );
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const optionLogoutHandler = () => {
    // handle logout
    handleClose();
    logoutHandler();
  };
  const initials = `${user.firstName ? user.firstName[0].toUpperCase() : ''}${
    user.lastName ? user.lastName[0].toUpperCase() : ''
  }`;

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const getInitials = (firstName, lastName) => `${firstName.charAt(0)}${lastName.charAt(0)}`;

  return (
    <Box display="flex" justifyContent="flex-start" alignItems="center" gap="3.5px">
      {renderAvatar()}
      <Box display="flex" gap="2.5px">
        <Typography variant={!isBreadcrumb ? 'calloutRegular' : 'h6'} color="primary">
          {isSmallScreen
            ? getInitials(user.firstName, user.lastName).toUpperCase()
            : `${user.firstName} ${user.lastName}`}
        </Typography>
        {type === UserType.COACH && (
          <ImageButton
            onClick={handleClick}
            src="/assets/icons/alt-arrow-down.svg"
            alt="chevron-down"
          />
        )}
      </Box>
      <Box>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          sx={{ '& .MuiPaper-root': { borderRadius: '10px' } }}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          <Box
            sx={{
              overflow: 'hidden',
              display: 'flex',
              padding: '10px',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minWidth: '200px',
              maxWidth: '250px',
            }}
          >
            {user.profilePic ? (
              <img
                src={getProfilePicUrl()}
                alt="user_image"
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  margin: '5px',
                }}
              />
            ) : (
              <Box
                sx={{
                  borderRadius: '50%',
                  backgroundColor: 'secondary.main',
                  color: 'white',
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {initials}
              </Box>
            )}
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{ position: 'absolute', top: 0, right: 0 }}
            >
              <img
                src="/assets/icons/close-circle-red.svg"
                alt="close-icon"
                style={{
                  cursor: 'pointer',
                }}
              />
            </IconButton>
            <Typography
              variant="body1"
              fontWeight="bold"
              sx={{
                py: '5px',
                px: '15px',
                flexWrap: 'wrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '200px',
              }}
            >
              {user.firstName} {user.lastName}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                paddingBottom: '10px',
                px: '15px',
                flexWrap: 'wrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '200px',
              }}
            >
              {user.email}
            </Typography>
            <StyledLogOutButton onClick={optionLogoutHandler} fullWidth>
              {t('topbar.logout')}
            </StyledLogOutButton>
            {/* <MenuItem onClick={optionLogoutHandler}>{t('topbar.logout')}</MenuItem> */}
          </Box>
        </Menu>
      </Box>
    </Box>
  );
};

export default User;
